import {
    showExportErrorToast,
    showExportProgressToast,
    showExportSuccessToast,
    updateExportProgressToast,
} from '@/@portal/export/components/Toast';
import { tracking } from '@/lib/features/tracking';
import { toast } from 'react-toastify';

import { studioAIService } from '../services';

let activeExport = false;

export const EXPORT_DATA_TOOLTIP = 'Export the full data used to generate this response';

export function isExportInProgress(): boolean {
    return activeExport;
}

function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Starts a background query data export that survives React component unmounts.
 * The API streams the workbook; this downloads that file rather than building
 * one in the browser.
 */
export function startQueryDataExport(conversationId: string, messageId: string): void {
    if (activeExport) return;
    activeExport = true;

    const progressToastId = showExportProgressToast({
        title: 'Export Started',
        description: 'Preparing your data export...',
        progress: 0,
    });

    void (async () => {
        try {
            updateExportProgressToast(progressToastId, {
                title: 'Exporting the full dataset',
                description: 'Feel free to continue using Studio while this downloads.',
                progress: 15,
            });

            const { blob, truncated, rowCount } = await studioAIService.exportQueryData(
                conversationId,
                messageId,
            );

            updateExportProgressToast(progressToastId, {
                title: 'Exporting Data',
                description: 'Saving spreadsheet...',
                progress: 80,
            });

            const filename = `dani-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
            downloadBlob(blob, filename);

            toast.dismiss(progressToastId);
            const rowLabel = `${rowCount.toLocaleString()} rows`;
            if (truncated) {
                toast.warning(
                    `Export stopped at the safety cap after ${rowLabel}. The file contains that many rows.`,
                    { position: 'top-right', autoClose: 8000 },
                );
            } else {
                showExportSuccessToast({
                    title: 'Export Complete',
                    description: `Your data has been downloaded (${rowLabel}).`,
                });
            }
        } catch (error) {
            toast.dismiss(progressToastId);
            showExportErrorToast('Export Failed', 'Failed to export data. Please try again.');
            tracking.errorWithSource(error as Error, 'exportManager.startQueryDataExport', {
                conversationId,
                messageId,
            });
        } finally {
            activeExport = false;
        }
    })();
}
